import { useEffect } from 'react';
import { useTranslation } from 'react-i18next'
import logo from "../traderhub24-logo.svg";
import Footer from "./components/Footer";

interface ChangelogEntry {
  version: string;
  date: string;
  changes: string[];
}

function Changelog({ onBack, onNavigate }: { onBack: () => void; onNavigate?: (page: "home" | "changelog" | "privacy-policy" | "terms-and-conditions" | "risk-warning" | "documentation" | "contact" | "pricing") => void }) {
  const { t } = useTranslation('pages')
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const changelogEntries: ChangelogEntry[] = [
    {
      version: "3.3.0",
      date: "2026-02-24",
      changes: [
        "New dashboard design",
        "Various minor general improvements",
        "Added new analytic metrics"      ]
    },
    {
      version: "3.2.0",
      date: "2026-02-05",
      changes: [
        "New features and improvements",
        "Added Blog page"      ]
    },
    {
      version: "3.1.0",
      date: "2026-02-02",
      changes: [
        "New Notification System",
      ]
    },
    {
      version: "3.0.0",
      date: "2026-01-26",
      changes: [
        "Major release with significant improvements and new features"
      ]
    },
    {
      version: "2.7.3",
      date: "2026-01-24",
      changes: [
        "Improved URL structure",
        "Added real-time price infrastructure",
        "Improved Trade page structure",
        "Added exchange rates infrastructure"
      ]
    },
    {
      version: "2.7.2",
      date: "2026-01-20",
      changes: [
        "Improved Stripe payment/membership integration",
        "Enhanced All Trades and Analytics filters",
        "Added new indicators to user panel"
      ]
    },
    {
      version: "2.7.1",
      date: "2026-01-15",
      changes: [
        "Updated Pricing page",
        "Updated Documentation page",
        "Improved User Profile area",
        "Added social media account fields"
      ]
    },
    {
      version: "2.7.0",
      date: "2026-01-14",
      changes: [
        "Improved user management",
        "Fixed various minor bugs",
        "Added forget password feature",
        "Added payment integration with Stripe"
      ]
    },
    {
      version: "2.6.0",
      date: "2026-01-09",
      changes: [
        "Improved Contact, Support and Feedback system",
        "Redesigned Landing, Pricing and Tools pages",
        "Improved user membership types and membership management"
      ]
    },
    {
      version: "2.5.0",
      date: "2026-01-08",
      changes: [
        "Fixed Import feature bug",
        "Added new features to Social Trade page",
        "Improved Trade Pool and Followers management",
        "Improved All Trades page",
        "Various minor improvements and bug fixes"
      ]
    },
    {
      version: "2.4.2",
      date: "2026-01-07",
      changes: [
        "Various minor improvements and bug fixes"
      ]
    },
    {
      version: "2.4.1",
      date: "2026-01-05",
      changes: [
        "Various minor improvements and bug fixes"
      ]
    },
    {
      version: "2.4",
      date: "2025-12-23",
      changes: [
        "Added Feedback feature",
        "New Landing page",
        "Various minor improvements and bug fixes"
      ]
    },
    {
      version: "2.3.0",
      date: "2025-12-22",
      changes: [
        "Added End-of-Day (EOD) price data integration",
        "Added daily price change field",
        "Added multi-language support"
      ]
    },
    {
      version: "2.2.1",
      date: "2025-12-21",
      changes: [
        "Mobile design improvements"
      ]
    },
    {
      version: "2.2.0",
      date: "2025-12-19",
      changes: [
        "Added Social Trade feature"
      ]
    },
    {
      version: "2.1.0",
      date: "2025-12-17",
      changes: [
        "Updated Landing page",
        "New Documentation page with detailed instructions for using the app",
        "New Footer Back-end navigation",
        "Added Privacy Policy, Terms of Service, and Risk Warning pages",
        "Various minor improvements and bug fixes"
      ]
    },
    {
      version: "2.0.1",
      date: "2025-12-16",
      changes: [
        "Various minor improvements and bug fixes"
      ]
    },
    {
      version: "2.0.0",
      date: "2025-12-15",
      changes: [
        "Improved All Trades view",
        "Added Trade History feature",
        "Added Strategy pages",
        "Implemented symbol standardization",
        "Added Analysis page"
      ]
    },
    {
      version: "1.1.0",
      date: "2025-12-10",
      changes: [
        "Added membership integration",
        "Added Trading Journal feature"
      ]
    },
    {
      version: "1.0.0",
      date: "2025-12-02",
      changes: [
        "Initial release of TraderHub24",
        "Added Risk/Reward & Position Size calculator",
        "Added Win Rate Calculator",
        "Added Profit Factor Calculator",
        "Added Market Hours Tools with real-time status",
        "Integrated TradingView ticker tape widget"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-app-bg text-gray-900">
      {/* Header */}
      <header className="w-full py-3 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <img 
            src={logo} 
            alt="TraderHub24" 
            className="h-12 md:h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={onBack}
          />
          <button
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {t('changelog.back', { ns: 'common' })}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">{t('changelog.title')}</h1>
          <p className="text-gray-600 mb-8">
            {t('changelog.description')}
          </p>

          <div className="space-y-8">
            {changelogEntries.map((entry, index) => (
              <div key={index} className="border-l-4 border-orange-500 pl-4 md:pl-6 pb-6 last:pb-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mb-3">
                  <h2 className="text-lg md:text-xl font-semibold">Version {entry.version}</h2>
                  <span className="text-sm text-gray-500">{entry.date}</span>
                </div>
                <ul className="space-y-2">
                  {entry.changes.map((change, changeIndex) => (
                    <li key={changeIndex} className="text-sm md:text-base text-gray-700 flex items-start">
                      <span className="text-orange-500 mr-2">•</span>
                      <span>{change}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer currentPage="changelog" onNavigate={onNavigate || ((page) => { if (page === "home") onBack(); })} />
    </div>
  );
}

export default Changelog;

